import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Title } from 'react-admin';
import {
    Alert, Box, Button, CircularProgress, IconButton, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchSeoReport, refreshSeoReport } from './seoApi';
import { SeoReport } from './seoTypes';
import { prettyMonth } from './seoFormat';
import { SeoSummary } from './SeoSummary';
import { SeoPageGroups } from './SeoPageGroups';
import { SeoQueries } from './SeoQueries';
import { SeoExecSummary } from './SeoExecSummary';
import { SeoInsights } from './SeoInsights';

export const SeoPage = () => {
    const queryClient = useQueryClient();
    const { data, isLoading, error } = useQuery<SeoReport, Error>('seoReport', fetchSeoReport, {
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const [sel, setSel] = useState<number | null>(null);
    const [tab, setTab] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState<string | null>(null);

    // Default the picker to the most recent month once the report loads.
    useEffect(() => {
        if (data && sel == null) setSel(data.months.length - 1);
    }, [data, sel]);

    const handleRefresh = async () => {
        setRefreshing(true);
        setRefreshError(null);
        try {
            const fresh = await refreshSeoReport();
            queryClient.setQueryData('seoReport', fresh);
            setSel(fresh.months.length - 1);
        } catch (e) {
            setRefreshError((e as Error).message || 'Refresh failed');
        } finally {
            setRefreshing(false);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Title title="SEO" />
                <CircularProgress size={20} /> <Typography>Loading organic-search report…</Typography>
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Box sx={{ p: 3 }}>
                <Title title="SEO" />
                <Alert severity="error" sx={{ mb: 2 }}>
                    Could not load the SEO report: {error?.message || 'unknown error'}
                </Alert>
                <Button variant="outlined" startIcon={<RefreshIcon />} disabled={refreshing} onClick={() => void handleRefresh()}>
                    Retry (fresh pull)
                </Button>
                {refreshError && <Alert severity="error" sx={{ mt: 2 }}>{refreshError}</Alert>}
            </Box>
        );
    }

    const selIdx = sel ?? data.months.length - 1;

    return (
        <Box sx={{ p: 3, minWidth: 0, width: '100%' }}>
            <Title title="SEO" />
            {/* Month picker — top left, matches the Analytics / Monthly Recap page */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <IconButton onClick={() => setSel(selIdx - 1)} size="small" disabled={selIdx <= 0}>
                    <ChevronLeftIcon />
                </IconButton>
                <TextField
                    type="month"
                    value={data.months[selIdx]}
                    onChange={(e) => {
                        const i = data.months.indexOf(e.target.value);
                        if (i >= 0) setSel(i);
                    }}
                    size="small"
                    sx={{ width: 200 }}
                    inputProps={{ min: data.months[0], max: data.months[data.months.length - 1] }}
                />
                <IconButton onClick={() => setSel(selIdx + 1)} size="small" disabled={selIdx >= data.months.length - 1}>
                    <ChevronRightIcon />
                </IconButton>
                {selIdx < data.months.length - 1 && (
                    <Button size="small" onClick={() => setSel(data.months.length - 1)}>
                        Latest
                    </Button>
                )}
            </Box>

            {/* Title + Refresh — matches the Monthly Recap title row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5">Organic Search — {prettyMonth(data.months[selIdx], data.mtdMonth)}</Typography>
                <Button
                    variant="outlined"
                    startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    disabled={refreshing}
                    onClick={() => void handleRefresh()}
                >
                    Refresh
                </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {data.meta.rangeLabel} · generated {new Date(data.meta.generatedOn).toLocaleDateString()}
            </Typography>

            {data.meta.warning && <Alert severity="warning" sx={{ mb: 2 }}>{data.meta.warning}</Alert>}
            {refreshError && <Alert severity="error" sx={{ mb: 2 }}>{refreshError}</Alert>}

            {/* AI executive summary stays pinned above the tabs — generated on demand. */}
            <SeoExecSummary key={data.months[selIdx]} report={data} sel={selIdx} />

            {/* Sections are tabbed so the page opens on Summary, not a wall of tables. */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mt: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Summary" />
                <Tab label="Insights" />
                <Tab label="By page group" />
                {data.queries.length > 0 && <Tab label="Search queries" />}
            </Tabs>

            {tab === 0 && <SeoSummary report={data} sel={selIdx} />}
            {tab === 1 && <SeoInsights report={data} sel={selIdx} />}
            {tab === 2 && <SeoPageGroups report={data} sel={selIdx} />}
            {tab === 3 && data.queries.length > 0 && <SeoQueries report={data} sel={selIdx} />}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4 }}>
                All figures are for the selected month, with month-over-month change vs the prior month.{' '}
                {data.months[selIdx] === data.mtdMonth && data.meta.priorMtdLabel
                    ? `Because this month is still in progress, its MoM compares the same number of days last month (${data.meta.priorMtdLabel}) — an apples-to-apples month-to-date comparison. `
                    : ''}
                The Trend sparkline
                always spans the full {data.months.length}-month data range. Impressions &amp; clicks: Google Search Console
                (domain property). CTR = clicks ÷ impressions. Avg pos = average Google ranking, impression-weighted (lower is
                better; — = no impressions). Visits = GA4 non-paid sessions landing on the page (the reach metric for pages
                with little search presence, e.g. Obituaries). Form fills: rows in <code>form_submissions</code>. Phone
                clicks: rows in <code>phone_click_events</code>. Form starts: GA4 enhanced measurement, and directional
                only — it is the one conversion metric with no first-party record behind it. All three cover non-paid
                traffic (every channel except Paid Search) and are attributed to the session landing page. A —
                means the metric was not being recorded that month, never that it was zero.
            </Typography>
        </Box>
    );
};

export default SeoPage;
